// tslint:disable:max-file-line-count
import {
  CalendarForm,
  CalendarInfo,
  CalendarSlot,
  GuestProfile,
  GuestProfileForm,
  Meeting
} from "@closerplatform/spinner-openapi";
import {
  addDays,
  addWeeks,
  endOfWeek,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  startOfWeek,
  subWeeks
} from "date-fns";
import { cast, flow, Instance, SnapshotIn, types } from "mobx-state-tree";
import { ID } from "@closerplatform/closer-sdk/dist/protocol/protocol";
import { logger } from "@fidget/common/utils";
import { FormField } from "../../containers/calendar/dropdownMeetingEdit";
import { HOUR_MILLIS, TimeUtils } from "@fidget/common/utils/timeUtils";
import { gapiConfig } from "../../gapiConfig";
import { NumberPair } from "../types";
import { servicesGetters, calendarStoreGetter, currentUserProfileGetter } from "../utils";
import { CalendarGuest, ICalendarGuest } from "./CalendarGuest";
import { CalendarMeeting, ICalendarMeeting } from "./CalendarMeeting";
import { CalendarTrash } from "./CalendarTrash";
import { CalendarEventListener } from "./eventHandlers";

import GoogleAuth = gapi.auth2.GoogleAuth;
import {
  editState,
  isEdit,
  isReschedule,
  isSchedule,
  isScheduleExisting,
  isScheduleNew,
  isView,
  MeetingPopup,
  Mode,
  rescheduleState,
  scheduleExistingState,
  scheduleNewState,
  scheduleState,
  searchState,
  viewState
} from "./MeetingPopup";
import { IGuest } from "../base/Guest";

export const GoogleCalendarSlot = types.model("CalendarSlot", {
  start: types.number,
  duration: types.number,
  title: types.maybe(types.string)
});

export const DraggingMeeting = types
  .model("DraggingMeeting", {
    start: NumberPair,
    parentOffset: NumberPair,
    initialWidth: types.number,
    calendarPosition: types.maybe(types.Date),
    roomId: types.string,
    // flag indicates whether the dragging entry enters trashBar
    inTrashBar: types.boolean,
    // meeting if exists or empty if a new meeting is being created.
    meeting: types.maybe(types.reference(CalendarMeeting))
  })
  .views(calendarStoreGetter)
  .views((self) => ({
    get guest(): IGuest | undefined {
      return self.calendarStore.guestsByRoom.get(self.roomId);
    }
  }))
  .actions((self) => ({
    mouseLeftCalendarEntry() {
      self.calendarPosition = undefined;
    },
    mouseEnteredCalendarEntry(time: Date) {
      self.calendarPosition = time;
    },
    mouseEnteredTrashBar() {
      self.inTrashBar = true;
    },
    mouseLeftTrashBar() {
      self.inTrashBar = false;
    }
  }));

export interface IDraggingMeeting extends Instance<typeof DraggingMeeting> {}

export const CalendarStore = types
  .model("CalendarStore", {
    from: types.optional(types.number, () =>
      startOfWeek(Date.now(), { weekStartsOn: 1 }).getTime()
    ),
    to: types.optional(types.number, () => endOfWeek(Date.now(), { weekStartsOn: 1 }).getTime()),
    meetings: types.array(CalendarMeeting),
    schedulingDisabled: types.maybe(types.boolean),
    guests: types.map(CalendarGuest),
    googleSlots: types.array(GoogleCalendarSlot),
    // displayedDay (UI property) represents currently showed day in calendar (a point of orientation).
    displayedDay: types.optional(types.Date, () => new Date()),

    // trash to handle delete/undo operations.
    trash: CalendarTrash,
    // existing or about to be created meeting dragged by the agent.
    draggingMeeting: types.maybe(DraggingMeeting),
    // popup to handle operation on calendar.
    popup: types.maybe(MeetingPopup)
  })
  .volatile((self) => ({
    googleApi: (window as any).gapi as typeof gapi
  }))
  .views(servicesGetters)
  .views(currentUserProfileGetter)
  .views((self) => ({
    filterByDay(day: Date) {
      return {
        meetings: self.meetings.filter((m) => isSameDay(m.start, day)),
        googleMeetings: self.googleSlots.filter((m) => isSameDay(m.start, day))
      };
    },
    get guestsByRoom(): Map<string, ICalendarGuest> {
      return new Map(
        [...self.guests.values()]
          .filter((g) => !!g.roomId)
          .map((g): [string, ICalendarGuest] => [g.roomId!, g])
      );
    },
    /**
     * Whether the requested date is in the supported range, thus the store loaded meetings from this period.
     */
    isInSupportedRange(date: number | Date) {
      return (
        isEqual(date, self.from) ||
        isEqual(date, self.to) ||
        (isBefore(date, self.to) && isAfter(date, self.from))
      );
    },
    get importedCalendar(): string | undefined {
      return self.currentUserProfile!.importedCalendar;
    }
  }))
  .actions((self) => {
    let trashBarTimeout: number;
    let initialized = false;

    /**
     * @param days the number of days an user jump forward or go back.
     * @return number of weeks.
     */
    const calculateNumberOfWeeksToExpand = (days: number) => {
      if (days < 4) {
        return 1;
      } else if (days / 7 < 4) {
        return 4;
      } else {
        // tslint:disable-next-line: no-bitwise
        return (days / 7 + 1) | 0;
      }
    };

    /**
     * Change the currently displayed day in calendar.
     * Be aware that the action is also responsible for loading missing meetings if user navigates
     * outside of the supported scope (self.from/self.to)
     *
     * @param days the number of days that should be added to/subtracted from the recently showed day.
     *  The default value '0' sets current day.
     */
    const navigateTo = (days = 0) => {
      if (!days) {
        self.displayedDay = new Date();
      } else {
        self.displayedDay = addDays(self.displayedDay, days);
      }

      if (self.isInSupportedRange(self.displayedDay)) {
        return;
      }

      const weeks = calculateNumberOfWeeksToExpand(Math.abs(days));
      if (days > 0) {
        const newTo = addWeeks(
          endOfWeek(self.displayedDay, { weekStartsOn: 1 }),
          weeks - 1
        ).getTime();
        expandSupportedRangeIfNeeded(newTo);
      } else if (days < 0) {
        const newFrom = subWeeks(
          startOfWeek(self.displayedDay, { weekStartsOn: 1 }),
          weeks - 1
        ).getTime();
        expandSupportedRangeIfNeeded(newFrom);
      }
    };

    /**
     * Expand the range if given date is outside of the supported range.
     */
    const expandSupportedRangeIfNeeded = (date: number) => {
      if (self.isInSupportedRange(date)) {
        return;
      }

      if (isBefore(date, self.from)) {
        loadMeetings(date, self.from);
        self.from = date;
      } else {
        loadMeetings(self.to, date);
        self.to = date;
      }
    };

    const createMeetingRequest = flow(function* (
      room: ID,
      start: Date,
      duration: number,
      rescheduled?: string
    ) {
      const spinnerService = self.spinnerService;
      try {
        self.popup = undefined;
        if (rescheduled) {
          yield spinnerService.rescheduleMeeting(rescheduled, start.getTime(), duration);
        } else {
          yield spinnerService.scheduleMeeting(room, start.getTime(), duration);
        }
        self.trash.hide();
      } catch (error) {
        logger.error({ message: "Couldn't create meeting request", error });
        throw new Error(`Could not schedule/reschedule a meeting for the room: '${room}'`);
      }
    });

    const cancelMeetingRequest = flow(function* (meeting: string) {
      try {
        yield self.spinnerService.cancelMeeting(meeting);
        self.popup = undefined;
      } catch (error) {
        logger.error({ message: "Couldn't cancel meeting request", error });
        throw new Error(`Could not cancel the meeting with id: '${meeting}'`);
      }
    });

    const changeMeetingStartTime = (start: Date) => {
      const { popup } = self;
      if (popup && (isEdit(popup) || isScheduleNew(popup) || isScheduleExisting(popup))) {
        if (isNaN(start.getTime()) && !isNaN(popup.start.getTime())) {
          popup.lastValidStart = popup.start;
        }
        popup.start = start;
      }
    };

    const changeMeetingDuration = (duration: number) => {
      const { popup } = self;
      if (popup && (isEdit(popup) || isScheduleNew(popup) || isScheduleExisting(popup))) {
        popup.duration = duration;
      }
    };

    const dismissPopup = () => {
      self.popup = undefined;
    };

    const createEditPopup = (field: FormField) => {
      const { popup } = self;
      if (popup) {
        if (isView(popup) || isReschedule(popup)) {
          self.popup = MeetingPopup.create({
            ...editState(
              popup.roomId,
              popup.start,
              popup.lastValidStart,
              popup.duration,
              field,
              popup.selected
            )
          });
        } else if (isSchedule(popup)) {
          self.popup = MeetingPopup.create({
            ...editState(popup.roomId, popup.start, popup.lastValidStart, popup.duration, field)
          });
        }
      }
    };

    const switchPopupToViewMode = () => {
      if (self.popup) {
        self.popup.mode = Mode.VIEW;
      }
    };

    const switchPopupToDeleteMode = () => {
      if (self.popup) {
        self.popup.mode = Mode.DELETE;
      }
    };

    const createExternalMeetingRequest = flow(function* (
      start: number,
      duration: number,
      form: GuestProfileForm
    ) {
      try {
        yield self.spinnerService.scheduleMeetingExternal(start, duration, form);
        self.trash.hide();
        self.popup = undefined;
      } catch (error) {
        logger.error({ message: "Couldn't create external meeting request", error });
        throw new Error("Could not create an external meeting");
      }
    });

    const createPopupWithGuest = (profile: GuestProfile) => {
      if (!self.guests.has(profile.id)) {
        self.guests.put({ ...profile });
      }
      const guest = self.guests.get(profile.id) as ICalendarGuest;
      const freeSlot = TimeUtils.firstAvailableSlot(self.meetings, HOUR_MILLIS);
      self.popup = cast({ ...scheduleExistingState(freeSlot, HOUR_MILLIS, guest) } as any);
    };

    const createPopupWithNewGuest = () => {
      const freeSlot = TimeUtils.firstAvailableSlot(self.meetings, HOUR_MILLIS);
      self.popup = MeetingPopup.create({ ...scheduleNewState(freeSlot, HOUR_MILLIS) });
    };

    const createSearchPopup = () => {
      self.popup = MeetingPopup.create({ ...searchState() });
    };

    const createSchedulePopup = (roomId: ID) => {
      const duration = HOUR_MILLIS;
      const start = TimeUtils.firstAvailableSlot(self.meetings, HOUR_MILLIS);
      self.popup = MeetingPopup.create({ ...scheduleState(roomId, start, duration) });
    };

    const createViewPopup = (meeting: ICalendarMeeting) => {
      self.popup = MeetingPopup.create({
        ...viewState(meeting.roomId, new Date(meeting.start), meeting.duration, meeting.id)
      });
    };

    const calendarEntryDragStarted = (
      start: [number, number],
      parentOffset: [number, number],
      initialWidth: number,
      roomId: ID,
      existing?: ICalendarMeeting
    ) => {
      const existingRefId = existing ? existing.id : undefined;
      self.draggingMeeting = DraggingMeeting.create({
        start,
        parentOffset,
        roomId,
        meeting: existingRefId,
        calendarPosition: undefined,
        initialWidth,
        inTrashBar: false
      });

      if (existing) {
        clearTimeout(trashBarTimeout);
        self.trash.prompt();
      }
    };

    const calendarEntryDragEnded = flow(function* () {
      const { draggingMeeting: dragState } = self;
      if (!dragState) {
        return;
      }

      const duration =
        (dragState && dragState.meeting && dragState.meeting.duration) || HOUR_MILLIS;
      const otherMeetings = self.meetings.filter((meeting) => meeting !== dragState.meeting);
      if (
        dragState.calendarPosition &&
        dragState.calendarPosition.getTime() > Date.now() &&
        !TimeUtils.overlapsWithMeeting(otherMeetings, dragState.calendarPosition, duration)
      ) {
        self.popup = MeetingPopup.create(
          dragState.meeting
            ? {
              ...rescheduleState(
                dragState.roomId,
                dragState.calendarPosition,
                duration,
                dragState.meeting.id
              )
            }
            : { ...scheduleState(dragState.roomId, dragState.calendarPosition, duration) }
        );
        self.trash.hide();
        self.draggingMeeting = undefined;
      } else if (dragState.meeting && dragState.inTrashBar) {
        self.trash.undo(dragState.meeting);
        const id = dragState.meeting.id;
        dragState.meeting = undefined;
        yield self.spinnerService.cancelMeeting(id);
        self.draggingMeeting = undefined;
        yield new Promise<void>((resolved) =>
          setTimeout(() => {
            self.trash.hide();
            resolved();
          }, 5000)
        );
      } else {
        self.draggingMeeting = undefined;
        self.trash.hide();
      }
    });

    const loadGoogleApi: () => Promise<GoogleAuth> = function () {
      return new Promise<GoogleAuth>((resolve) => {
        self.googleApi.load("auth2", () => {
          let gapiInstance = self.googleApi.auth2.getAuthInstance();
          if (!gapiInstance) {
            gapiInstance = gapi.auth2.init(gapiConfig);
          }
          resolve(gapiInstance);
        });
      });
    };

    const googleSignIn: () => Promise<Array<CalendarInfo>> = flow(function* () {
      try {
        const auth2 = yield loadGoogleApi();
        const { code } = yield auth2.grantOfflineAccess({ scope: gapiConfig.scope || "" });
        return yield self.spinnerService.authorizeCode({ authCode: code });
      } catch {
        const auth2 = self.googleApi.auth2 && self.googleApi.auth2.getAuthInstance();
        if (auth2) {
          auth2.disconnect();
          auth2.signOut();
        }
        throw Error("Could not sign in to Google");
      }
    });

    const googleSignOut: () => Promise<void> = flow(function* () {
      let auth2 = self.googleApi.auth2 && self.googleApi.auth2.getAuthInstance();
      if (!auth2) {
        auth2 = yield loadGoogleApi();
      }
      try {
        yield auth2.signOut();
        yield auth2.disconnect();
      } catch {
        throw Error("Could not sign out to Google");
      }
    });

    const getCalendarsFromGoogle: () => Promise<Array<CalendarInfo>> = flow(function* () {
      try {
        return yield self.spinnerService.getCalendars();
      } catch {
        throw Error("Could not sign out to Google");
      }
    });

    const setImportedCalendar: (calendarForm: CalendarForm) => Promise<any> = flow(function* (
      calendarForm: CalendarForm
    ) {
      try {
        const googleSlots = yield self.spinnerService.importEvents(calendarForm);
        self.currentUserProfile!.setImportedCalendar(calendarForm.calendarName);
        self.googleSlots = googleSlots.map((slot: CalendarSlot) => ({ ...slot }));
      } catch (reason) {
        throw Error("Could not get slots from Google");
      }
    });

    const stopSyncGoogleCalendar: () => Promise<any> = flow(function* () {
      try {
        yield self.spinnerService.deleteSlots();
        self.currentUserProfile!.setImportedCalendar(undefined);
        self.googleSlots.clear();
      } catch (reason) {
        throw Error("Could not delete slots from Google");
      }
    });

    const loadGuestsForRooms = flow(function* (rooms: Array<string>) {
      // filter duplication and already loaded entries
      const roomIds = [...new Set(rooms)].filter((id) => !self.guestsByRoom.has(id));
      // TODO: requires optimization on backend. => CLSR-2912
      const guests: GuestProfile[] = yield Promise.all(
        roomIds.map((roomId) => self.spinnerService.getGuestProfileByRoom(roomId))
      );

      self.guests.merge(guests.map((guest) => [guest.id, { ...guest }]));
    });

    /**
     * Add meeting if it fits to the supported range, otherwise ignore.
     */
    const addMeeting = (snapshot: SnapshotIn<typeof CalendarMeeting>) => {
      if (
        self.isInSupportedRange(snapshot.start) ||
        self.isInSupportedRange(snapshot.start + snapshot.duration)
      ) {
        self.meetings.push(snapshot);
        loadGuestsForRooms([snapshot.roomId]);
      }
    };

    const removeMeeting = (id: string) => {
      const index = self.meetings.findIndex((m) => m.id === id);
      if (index > -1) {
        self.meetings.splice(index, 1);
      }
    };

    const updateMeeting = ({
      id,
      start,
      duration
    }: {
      id: string;
      start: number;
      duration: number;
    }) => {
      const meeting = self.meetings.find((m) => m.id === id);
      if (meeting) {
        meeting.start = start;
        meeting.duration = duration;
      }
    };

    const loadGoogleSlots = flow(function* () {
      try {
        const googleSlots = yield self.spinnerService.getSlots();
        self.googleSlots = googleSlots.map((slot: CalendarSlot) => ({ ...slot }));
      } catch (error) {
        logger.error({ message: "Coudln't load google slots", error });
      }
    });

    const loadMeetings = flow(function* (from: number, to: number) {
      try {
        const meetings = yield self.spinnerService.getMeetings(from, to);
        meetings.forEach((meeting: Meeting) =>
          self.meetings.push(Object.assign({ roomId: meeting.room }, meeting))
        );
        const rooms = meetings.map((meeting: Meeting) => meeting.room);
        loadGuestsForRooms(rooms);
      } catch (error) {
        logger.error({ message: "Couldn't load meetings", error });
        throw new Error("Couldn't load meetings.");
      }
    });

    /**
     * Initialize the store.
     */
    const init = flow(function* () {
      try {
        if (self.importedCalendar) {
          yield loadGoogleSlots();
        }
        yield loadMeetings(self.from, self.to);
      } catch (error) {
        logger.error({ message: "Couldn't initalize calendar store", error });
        throw new Error("Could not initialize the calendar store.");
      }
    });

    /**
     * On refreshing store, calculate the possible range on the basis of the current time and self.displayedDate.
     */
    const resetRange = () => {
      const date = Date.now();
      if (isBefore(date, self.displayedDay)) {
        self.from = startOfWeek(date, { weekStartsOn: 1 }).getTime();
        self.to = endOfWeek(self.displayedDay, { weekStartsOn: 1 }).getTime();
      } else {
        self.from = startOfWeek(self.displayedDay, { weekStartsOn: 1 }).getTime();
        self.to = endOfWeek(date, { weekStartsOn: 1 }).getTime();
      }
    };

    /**
     * Refresh existing calendar state.
     */
    const refresh = flow(function* () {
      try {
        resetRange();
        const meetings: Meeting[] = yield self.spinnerService.getMeetings(self.from, self.to);
        const meetingsMap = new Map(
          meetings.map((meeting): [string, Meeting] => [meeting.id, meeting])
        );

        // remove non-existing anymore elements.
        self.meetings.replace(self.meetings.filter((meeting) => meetingsMap.has(meeting.id)));

        // update the state of the existing.
        self.meetings.forEach((meeting) => {
          const { start, duration } = meetingsMap.get(meeting.id)!;
          Object.assign(meeting, { start, duration });
          meetingsMap.delete(meeting.id);
        });

        // append new elements
        meetingsMap.forEach((m) => self.meetings.push(Object.assign({ roomId: m.room }, m)));
        const rooms = self.meetings.map((meeting) => meeting.roomId);
        loadGuestsForRooms(rooms);
      } catch (error) {
        logger.error({ message: "Couldn't refresh calendar", error });
        throw new Error("Couldn't reload calendarStore.");
      }
    });

    /**
     * Setup the calendar store. If the calendar has already have its state initialized setup will synchronize
     * the state with the newest data, otherwise the full initialization is performed.
     */
    const setup = flow(function* () {
      if (initialized === true) {
        yield refresh();
      } else {
        yield init();
        initialized = true;
      }
    });

    const setSchedulingDisabled = (isSchedulingDisabled: boolean) => {
      self.schedulingDisabled = isSchedulingDisabled;
    };

    return {
      // calendar navigation
      navigateTo,
      expandSupportedRangeIfNeeded,
      // requests
      createMeetingRequest,
      createExternalMeetingRequest,
      cancelMeetingRequest,
      // operation on store
      addMeeting,
      removeMeeting,
      updateMeeting,
      // popup actions
      dismissPopup,
      createEditPopup,
      createSearchPopup,
      createSchedulePopup,
      createViewPopup,
      createPopupWithGuest,
      createPopupWithNewGuest,
      switchPopupToViewMode,
      switchPopupToDeleteMode,
      changeMeetingStartTime,
      changeMeetingDuration,
      // draggable events
      calendarEntryDragStarted,
      calendarEntryDragEnded,
      // google calendar
      // checkIfAgentIsSignedToGoogle,
      googleSignIn,
      googleSignOut,
      getCalendarsFromGoogle,
      setImportedCalendar,
      stopSyncGoogleCalendar,
      // setup store
      setup,
      // misc
      setSchedulingDisabled
    };
  })
  ////////////////////////////
  // Event listener
  ////////////////////////////
  .actions((self) => {
    const calendarEventListenerName = "CalendarEventListener";
    return {
      afterCreate: () => {
        self.eventListener.addGlobalListener(
          new CalendarEventListener(() => self as ICalendarStore, calendarEventListenerName)
        );
      },
      beforeDestroy: () => {
        self.eventListener.removeGlobalListener(calendarEventListenerName);
      }
    };
  });

export interface ICalendarStore extends Instance<typeof CalendarStore> {}
